import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined"
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  getHrDashboard,
} from "../../services/hrDashboardService"


// =========================================================
// HELPERS
// =========================================================

const money = (value) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))} USD`


const number = (value) =>
  new Intl.NumberFormat("fr-FR").format(
    Number(value || 0)
  )


function getErrorMessage(error) {
  const detail =
    error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      "Impossible de charger le dashboard RH."
    )
  }

  return (
    error?.message ||
    "Impossible de charger le dashboard RH."
  )
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  accent = "#2563eb",
  iconBackground = "rgba(37, 99, 235, 0.10)",
  surfaceBackground = "#ffffff",
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",

        minWidth: 0,
        minHeight: 126,

        p: {
          xs: 1.45,
          sm: 1.65,
        },

        overflow: "hidden",

        border: "1px solid",
        borderColor:
          "rgba(148, 163, 184, 0.22)",

        borderRadius: 3,

        bgcolor:
          surfaceBackground,

        boxShadow:
          "0 7px 22px rgba(15, 23, 42, 0.035)",

        transition:
          "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",

        "&:hover": {
          transform:
            "translateY(-2px)",

          borderColor:
            "rgba(37, 99, 235, 0.20)",

          boxShadow:
            "0 12px 28px rgba(15, 23, 42, 0.07)",
        },

        "&::before": {
          content: '""',

          position: "absolute",

          left: 0,
          top: 0,
          bottom: 0,

          width: 4,

          bgcolor:
            accent,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.4,

            bgcolor:
              iconBackground,

            color:
              accent,

            display: "grid",
            placeItems: "center",

            "& .MuiSvgIcon-root":
              {
                fontSize: 22,
              },
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                "#64748b",

              fontWeight: 800,

              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,

              color:
                "#0f172a",

              fontSize: {
                xs: "1.45rem",
                sm: "1.6rem",
              },

              fontWeight: 950,

              lineHeight: 1.05,

              letterSpacing:
                "-0.025em",

              overflowWrap:
                "anywhere",
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: "block",

              mt: 0.7,

              color:
                "#64748b",

              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function HrDashboardPage() {
  const [data, setData] =
    useState({
      summary: {},
      attendance: {},
      contracts_expiring_30_days:
        0,
    })

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState("")


  // =======================================================
  // LOAD
  // =======================================================

  const loadDashboard =
    useCallback(async () => {
      setLoading(true)
      setError("")

      try {
        const result =
          await getHrDashboard()

        setData({
          summary:
            result?.summary ||
            {},

          attendance:
            result?.attendance ||
            {},

          contracts_expiring_30_days:
            Number(
              result?.contracts_expiring_30_days ||
                0
            ),
        })
      } catch (err) {
        console.error(
          "Erreur chargement dashboard RH :",
          err
        )

        setError(
          getErrorMessage(err)
        )
      } finally {
        setLoading(false)
      }
    }, [])


  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])


  // =======================================================
  // DATA
  // =======================================================

  const summary =
    data.summary || {}

  const attendance =
    data.attendance || {}


  const indicators =
    useMemo(
      () => ({
        totalEmployees:
          Number(
            summary.total_employees ||
              0
          ),

        activeEmployees:
          Number(
            summary.active_employees ||
              0
          ),

        presentToday:
          Number(
            attendance.present_today ||
              0
          ),

        absentToday:
          Number(
            attendance.absent_today ||
              0
          ),

        lateToday:
          Number(
            attendance.late_today ||
              0
          ),

        onLeaveToday:
          Number(
            attendance.on_leave_today ||
              0
          ),

        basePayroll:
          Number(
            summary.base_payroll ||
              0
          ),

        contractsExpiring:
          Number(
            data.contracts_expiring_30_days ||
              0
          ),
      }),
      [
        summary,
        attendance,
        data.contracts_expiring_30_days,
      ]
    )


  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,

          p: {
            xs: 1.25,
            sm: 1.75,
            md: 2.5,
          },

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 1.6,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color:
                  "#2563eb",

                fontWeight: 900,

                letterSpacing:
                  "0.08em",

                lineHeight: 1,
              }}
            >
              RESSOURCES HUMAINES
            </Typography>

            <Typography
              component="h1"
              sx={{
                mt: 0.55,

                color:
                  "#0f172a",

                fontSize: {
                  xs: "1.75rem",
                  sm: "2rem",
                  md: "2.2rem",
                },

                lineHeight: 1.08,

                letterSpacing:
                  "-0.035em",

                fontWeight: 950,
              }}
            >
              Dashboard RH
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.55,

                maxWidth: 860,

                color:
                  "#64748b",

                lineHeight: 1.55,
              }}
            >
              Vue consolidée de
              l’effectif, des présences,
              absences, congés,
              retards, masse salariale
              et contrats à échéance.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={
              loading ? (
                <CircularProgress
                  size={17}
                />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={
              loadDashboard
            }
            disabled={
              loading
            }
            sx={{
              minWidth: {
                xs: "100%",
                sm: 132,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            {loading
              ? "Actualisation..."
              : "Actualiser"}
          </Button>
        </Box>


        {/* =================================================
            STATUS MESSAGE
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            mb: 1.6,

            px: {
              xs: 1.35,
              sm: 1.6,
            },

            py: 1.15,

            borderRadius: 3,

            border:
              "1px solid rgba(37, 99, 235, 0.12)",

            bgcolor:
              "rgba(239, 246, 255, 0.72)",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color:
                    "#1e3a8a",

                  fontWeight: 900,
                }}
              >
                Pilotage RH centralisé
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",

                  mt: 0.2,

                  color:
                    "#64748b",
                }}
              >
                Les indicateurs ci-dessous
                sont consolidés à partir
                des données RH disponibles
                dans RLSys Business.
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color:
                  loading
                    ? "#d97706"
                    : "#15803d",

                fontWeight: 900,

                whiteSpace:
                  "nowrap",
              }}
            >
              {loading
                ? "Mise à jour en cours"
                : "Données chargées"}
            </Typography>
          </Stack>
        </Paper>


        {error && (
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              mb: 1.6,
              borderRadius: 3,
            }}
          >
            {error}
          </Alert>
        )}


        {/* =================================================
            EFFECTIF
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            mb: 1.6,

            p: {
              xs: 1.3,
              sm: 1.5,
            },

            borderRadius: 3.5,

            border:
              "1px solid rgba(148, 163, 184, 0.22)",

            bgcolor:
              "#ffffff",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              mb: 1.2,
            }}
          >
            <Typography
              sx={{
                color:
                  "#0f172a",

                fontWeight: 900,
              }}
            >
              Effectif
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color:
                  "#64748b",
              }}
            >
              Situation générale des
              collaborateurs enregistrés.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm:
                  "repeat(2, minmax(0, 1fr))",
              },

              gap: 1,
            }}
          >
            <KpiCard
              title="Effectif total"
              value={number(
                indicators.totalEmployees
              )}
              subtitle="Employés enregistrés"
              accent="#2563eb"
              iconBackground="rgba(37, 99, 235, 0.10)"
              icon={
                <GroupsOutlinedIcon />
              }
            />

            <KpiCard
              title="Employés actifs"
              value={number(
                indicators.activeEmployees
              )}
              subtitle="Effectif actuellement en activité"
              accent="#16a34a"
              iconBackground="rgba(22, 163, 74, 0.10)"
              icon={
                <BadgeOutlinedIcon />
              }
            />
          </Box>
        </Paper>


        {/* =================================================
            PRESENCE
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            mb: 1.6,

            p: {
              xs: 1.3,
              sm: 1.5,
            },

            borderRadius: 3.5,

            border:
              "1px solid rgba(148, 163, 184, 0.22)",

            bgcolor:
              "#ffffff",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              mb: 1.2,
            }}
          >
            <Typography
              sx={{
                color:
                  "#0f172a",

                fontWeight: 900,
              }}
            >
              Présence du jour
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color:
                  "#64748b",
              }}
            >
              Présences, absences,
              retards et congés
              enregistrés aujourd’hui.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm:
                  "repeat(2, minmax(0, 1fr))",
                xl:
                  "repeat(4, minmax(0, 1fr))",
              },

              gap: 1,
            }}
          >
            <KpiCard
              title="Présents"
              value={number(
                indicators.presentToday
              )}
              subtitle="Présence enregistrée aujourd’hui"
              accent="#16a34a"
              iconBackground="rgba(22, 163, 74, 0.10)"
              icon={
                <ScheduleOutlinedIcon />
              }
            />

            <KpiCard
              title="Absents"
              value={number(
                indicators.absentToday
              )}
              subtitle="Absences enregistrées aujourd’hui"
              accent="#dc2626"
              iconBackground="rgba(220, 38, 38, 0.09)"
              icon={
                <PersonOffOutlinedIcon />
              }
            />

            <KpiCard
              title="Retards"
              value={number(
                indicators.lateToday
              )}
              subtitle="Retards enregistrés aujourd’hui"
              accent="#d97706"
              iconBackground="rgba(217, 119, 6, 0.10)"
              icon={
                <ScheduleOutlinedIcon />
              }
            />

            <KpiCard
              title="En congé"
              value={number(
                indicators.onLeaveToday
              )}
              subtitle="Collaborateurs en congé aujourd’hui"
              accent="#7c3aed"
              iconBackground="rgba(124, 58, 237, 0.10)"
              icon={
                <BeachAccessOutlinedIcon />
              }
            />
          </Box>
        </Paper>


        {/* =================================================
            PAYROLL & CONTRACTS
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 1.3,
              sm: 1.5,
            },

            borderRadius: 3.5,

            border:
              "1px solid rgba(148, 163, 184, 0.22)",

            bgcolor:
              "#ffffff",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              mb: 1.2,
            }}
          >
            <Typography
              sx={{
                color:
                  "#0f172a",

                fontWeight: 900,
              }}
            >
              Paie & contrats
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color:
                  "#64748b",
              }}
            >
              Exposition salariale de base
              et échéances contractuelles
              à surveiller.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                lg:
                  "repeat(2, minmax(0, 1fr))",
              },

              gap: 1,
            }}
          >
            <KpiCard
              title="Masse salariale de base"
              value={money(
                indicators.basePayroll
              )}
              subtitle="Base salariale des employés actifs"
              accent="#2563eb"
              iconBackground="rgba(37, 99, 235, 0.10)"
              icon={
                <PaymentsOutlinedIcon />
              }
            />

            <KpiCard
              title="Contrats à échéance"
              value={number(
                indicators.contractsExpiring
              )}
              subtitle="Contrats arrivant à échéance dans les 30 prochains jours"
              accent={
                indicators.contractsExpiring >
                0
                  ? "#dc2626"
                  : "#16a34a"
              }
              iconBackground={
                indicators.contractsExpiring >
                0
                  ? "rgba(220, 38, 38, 0.09)"
                  : "rgba(22, 163, 74, 0.10)"
              }
              icon={
                <WarningAmberOutlinedIcon />
              }
            />
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  )
}
